// Senha pra "Liberar encerramento sem teste" (pedido 2026-10-15): o técnico
// testou o equipamento fora do sistema e o admin libera em Agenda → Chamados
// digitando esta senha. Enquanto ninguém criar uma, vale a senha padrão. Só o
// admin troca (o backend confere o accountType).
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Eye, EyeOff, KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { AdminAccessGate } from "@/components/admin-access-gate";
import { useAgendaSettings, useUpdateExternalTestPassword } from "@/hooks/queries/agenda";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";

export const Route = createFileRoute("/admin/technician-test-password")({
  component: () => (
    <AdminAccessGate action="conta_integracoes">
      <TechnicianTestPasswordPage />
    </AdminAccessGate>
  ),
  head: () => ({ meta: [{ title: "Senha para liberar sem teste — Administração — Wp Rastreamento" }] }),
});

function TechnicianTestPasswordPage() {
  const { data: settings, isLoading } = useAgendaSettings();
  const update = useUpdateExternalTestPassword();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setError(null);
    if (password.length < 6) return setError("A senha precisa ter pelo menos 6 caracteres.");
    if (password !== confirm) return setError("As duas senhas não são iguais.");
    try {
      await update.mutateAsync(password);
      setPassword("");
      setConfirm("");
      toast.success("Senha salva");
    } catch (err) {
      setError(err instanceof GpsDriveApiError ? err.message : "Não foi possível salvar a senha.");
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Senha para liberar sem teste</h1>
        <p className="text-sm text-muted-foreground">
          Quando o técnico testou o equipamento fora do sistema, use <strong>“Liberar sem teste”</strong> em Agenda →
          Chamados: esta senha é pedida e o técnico passa a conseguir encerrar sem testar o rastreador pelo app. Tudo
          fica no registro do atendimento.
        </p>
      </div>

      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <KeyRound className="h-4 w-4" /> Senha atual
            {!isLoading &&
              (settings?.externalTestPasswordCustom ? (
                <Badge className="border-transparent bg-emerald-500 text-white hover:bg-emerald-500">
                  <ShieldCheck className="mr-1 h-3 w-3" /> Personalizada
                </Badge>
              ) : (
                <Badge className="border-transparent bg-amber-500 text-white hover:bg-amber-500">Padrão</Badge>
              ))}
          </CardTitle>
          <CardDescription>
            {settings?.externalTestPasswordCustom
              ? "Você já criou uma senha própria. Para trocar, digite a nova abaixo."
              : "Ainda está valendo a senha padrão do sistema: Gpsdrive@2056. Crie uma só sua abaixo."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="ext-pass">Nova senha</Label>
            <div className="relative">
              <Input
                id="ext-pass"
                type={show ? "text" : "password"}
                value={password}
                autoComplete="new-password"
                onChange={(e) => setPassword(e.target.value)}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShow((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label={show ? "Esconder senha" : "Mostrar senha"}
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ext-pass-2">Repita a senha</Label>
            <Input
              id="ext-pass-2"
              type={show ? "text" : "password"}
              value={confirm}
              autoComplete="new-password"
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end">
            <Button onClick={save} disabled={update.isPending || !password}>
              {update.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar senha
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">Só o administrador da empresa pode trocar esta senha.</p>
        </CardContent>
      </Card>
    </div>
  );
}
